// One definition of "this cart is ready to pay" (24 Sep 2026, parity with BOO e1f3d66). Used by the checkout step
// logic, the review "Please complete" list, address saving (server action) and the backend 400 guard on payment
// sessions / cart completion. Keep the storefront and backend copies identical.

type Addr = {
  first_name?: string | null
  last_name?: string | null
  address_1?: string | null
  city?: string | null
  province?: string | null
  postal_code?: string | null
  country_code?: string | null
} | null | undefined

type CartLike = {
  email?: string | null
  shipping_address?: Addr
  billing_address?: Addr
  shipping_methods?: unknown[] | null
} | null | undefined

export type MissingItem = { label: string; step: "shipping-address" | "billing-address" | "delivery" | "contact-details" }

const AU_POSTCODE = /^\d{4}$/
const blank = (v?: string | null) => !String(v ?? "").trim()

export function addressProblems(a: Addr, who: "Delivery" | "Billing"): string[] {
  if (!a) return [`${who} address`]
  const out: string[] = []
  if (blank(a.first_name) || blank(a.last_name)) out.push(`${who} name`)
  if (blank(a.address_1)) out.push(`${who} street address`)
  if (blank(a.city)) out.push(`${who} suburb`)
  if (blank(a.province)) out.push(`${who} state`)
  if (blank(a.postal_code)) out.push(`${who} postcode`)
  else if ((a.country_code || "au").toLowerCase() === "au" && !AU_POSTCODE.test(String(a.postal_code).trim()))
    out.push(`${who} postcode (4 digits)`)
  if (blank(a.country_code)) out.push(`${who} country`)
  return out
}

/** Everything still missing before payment can be taken, each with the checkout step that fixes it. */
export function missingForCheckout(cart: CartLike): MissingItem[] {
  if (!cart) return [{ label: "Cart", step: "shipping-address" }]
  const items: MissingItem[] = []
  for (const l of addressProblems(cart.shipping_address, "Delivery")) items.push({ label: l, step: "shipping-address" })
  for (const l of addressProblems(cart.billing_address, "Billing")) items.push({ label: l, step: "billing-address" })
  if ((cart.shipping_methods?.length ?? 0) < 1) items.push({ label: "Delivery method", step: "delivery" })
  if (blank(cart.email)) items.push({ label: "Email", step: "contact-details" })
  return items
}
